import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import StripeCheckout from '../components/StripeCheckout';
import MercadoPagoLinkButton from '../components/MercadoPagoLinkButton';
import CustomerInfoForm from '../components/CustomerInfoForm';
import KioskPurchaseSuccessVideo from '../components/KioskPurchaseSuccessVideo';
import { getCustomerByEmail, registerCustomer } from '../services/customersService';
import { useKiosk } from '../contexts/KioskContext';
import { DELIVERY_FEE } from '../constants';
import { useLocale } from '../hooks/useLocale';
import { formatPrice as fmtPrice } from '../utils/format';
import { tField } from '../utils/i18nContent';
import { thumbnail as proxyThumb } from '../lib/imageProxy';
import { clarity } from '../lib/clarity';
import { normalizeInstagram } from '../utils/validation';
import { subscribeMailing } from '../services/mailingService';
import { createAllyOrder, canPayWithMercadoPagoLinks, canPayByAllyBankTransfer } from '../services/allyOrderService';
import { trackClick } from '../services/analyticsService';

const CheckoutPage = () => {
  const { items, totalItems, getTotalPrice, clearCart } = useCart();
  const { user: authUser, session } = useAuth();
  const { isKiosk } = useKiosk();
  const navigate = useNavigate();
  const locale = useLocale();
  const { t } = useTranslation('checkout');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showKioskVideo, setShowKioskVideo] = useState(false);
  const [updatingInventory, setUpdatingInventory] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [isCustomerInfoValid, setIsCustomerInfoValid] = useState(false);
  const [purchasedItems, setPurchasedItems] = useState([]);
  const [deliveryMethod, setDeliveryMethod] = useState('delivery'); // 'delivery' or 'personal'
  const [saveMyData, setSaveMyData] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [loadedCustomer, setLoadedCustomer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null); // ally bags: 'mercado_pago_link' | 'bank_transfer'
  const [allyOrder, setAllyOrder] = useState(null);
  const [allyOrderSubmitting, setAllyOrderSubmitting] = useState(false);
  const [allyOrderError, setAllyOrderError] = useState(null);
  const [copiedClabe, setCopiedClabe] = useState(null);

  // Ally products are never paid by card: only through their Mercado Pago
  // links or by bank transfer straight to the ally, each offered when every
  // bag line supports it. Kiosks don't open the external Mercado Pago link.
  // Ally and catalog products can't be paid together (card vs. ally methods).
  const isAllyBag = items.length > 0 && items.every((item) => item.source === 'ally');
  const isMixedBag = !isAllyBag && items.some((item) => item.source === 'ally');
  const allyPaymentOptions = isAllyBag ? [
    !isKiosk && canPayWithMercadoPagoLinks(items) && 'mercado_pago_link',
    canPayByAllyBankTransfer(items) && 'bank_transfer',
  ].filter(Boolean) : [];
  const selectedAllyPayment = allyPaymentOptions.includes(paymentMethod) ? paymentMethod : allyPaymentOptions[0];
  const allyNames = [...new Set(items.map((item) => item.ally_name).filter(Boolean))].join(', ');
  const requireInstagram = isAllyBag && selectedAllyPayment === 'bank_transfer';

  // Fire once per checkout visit, not per re-render (funnel drop-off signal
  // for Clarity — see docs/ui:ux_feature.md §2.2).
  useEffect(() => {
    clarity.event('checkout_started');
  }, []);

  // When user is logged in, preload customer data by email
  useEffect(() => {
    if (!authUser?.email) return;
    let cancelled = false;
    getCustomerByEmail(authUser.email)
      .then(({ customer }) => {
        if (!cancelled && customer) {
          setLoadedCustomer({ ...customer, zipCode: customer.zipCode ?? customer.zip_code });
        } else if (!cancelled) {
          // No Customers row: prefill from auth (email, name, phone, instagram from user_metadata)
          const name = authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || '';
          const phone = authUser.user_metadata?.phone || '';
          const instagram = authUser.user_metadata?.instagram || '';
          setLoadedCustomer({
            email: authUser.email,
            name: name || undefined,
            phone: phone || undefined,
            instagram: instagram || undefined,
            zipCode: undefined
          });
        }
      })
      .catch(() => { if (!cancelled) setLoadedCustomer(null); });
    return () => { cancelled = true; };
  }, [authUser]);


  const formatPrice = (price) => fmtPrice(price, 'MXN', locale);

  // Calculate total with delivery fee (only for delivery, not personal)
  const getTotalWithDelivery = () => {
    if (deliveryMethod === 'personal') {
      return getTotalPrice(); // No delivery fee for personal delivery
    }
    return getTotalPrice() + DELIVERY_FEE;
  };

  const handleCustomerInfoChange = (info, isValid) => {
    setCustomerInfo(info);
    setIsCustomerInfoValid(isValid);
  };

  // Shared by card and Mercado Pago link orders: normalizes the customer
  // payload, fires the mailing opt-in and resolves the Customers row to link.
  const prepareCustomer = async (customerData) => {
    // Asked for in-store pickup and ally bank transfers (it's the transfer's
    // concepto); the form already rejected invalid handles
    const instagram = deliveryMethod === 'personal' || requireInstagram
      ? (normalizeInstagram(customerData?.instagram) || undefined)
      : undefined;

    // Split name into firstName / lastName for the API payload
    const nameParts = (customerData?.name || '').trim().split(' ');
    const customerInfoForAPI = {
      ...customerData,
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      name: customerData?.name || '',
      instagram,
    };

    // Raffle / event / newsletter opt-in. Non-blocking; a guest (or another
    // address than the session's) gets a double opt-in email.
    if (marketingOptIn && customerData?.email) {
      subscribeMailing(
        { email: customerData.email, name: customerData.name, source: 'checkout', locale },
        session?.access_token,
      ).catch((err) => console.error('Mailing opt-in failed (non-blocking):', err));
    }

    // ------------------------------------------------------------------
    // Resolve customerId for linking the purchase record to a customer row
    // ------------------------------------------------------------------
    let customerId = null;
    if (saveMyData && customerData?.email) {
      try {
        const reg = await registerCustomer({
          email: customerData.email,
          name: customerData.name,
          phone: customerData.phone,
          address: customerData.address,
          city: customerData.city,
          state: customerData.state,
          zipCode: customerData.zipCode,
          country: customerData.country || 'México',
          instagram,
        });
        if (reg?.customer?.id) customerId = reg.customer.id;
      } catch (regErr) {
        console.error('Customer registration failed (non-blocking):', regErr);
      }
    } else if (authUser?.email) {
      if (loadedCustomer?.id) {
        customerId = loadedCustomer.id;
      } else {
        try {
          const reg = await registerCustomer({
            email: customerData?.email || authUser.email,
            name: customerData?.name,
            phone: customerData?.phone,
            address: customerData?.address,
            city: customerData?.city,
            state: customerData?.state,
            zipCode: customerData?.zipCode,
            country: customerData?.country || 'México',
            instagram,
          });
          if (reg?.customer?.id) customerId = reg.customer.id;
        } catch (regErr) {
          console.error('Customer registration failed (non-blocking):', regErr);
        }
      }
    }

    return { customerInfoForAPI, customerId };
  };

  const handlePaymentSuccess = async (intent) => {
    try {
      // Snapshot cart and customer data before any state changes
      const cartItems = [...items];
      const customerData = customerInfo;

      setUpdatingInventory(true);

      const { customerInfoForAPI, customerId } = await prepareCustomer(customerData);

      // ------------------------------------------------------------------
      // Single call: save purchase record + decrement stock atomically.
      // Replaces the previous two-call sequence that could leave the DB in
      // an inconsistent state if the browser closed between them.
      // ------------------------------------------------------------------
      try {
        const completeResponse = await fetch('/api/complete-purchase', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerInfo: customerInfoForAPI,
            paymentIntent: {
              id: intent.id,
              amount: intent.amount,
              currency: intent.currency,
              status: intent.status,
            },
            items: cartItems.map(item => ({
              id: item.id,
              name: item.name,
              quantity: item.quantity || 1,
              price: item.price,
              image: item.image || null,
              source: item.source || undefined,
              variantId: item.variantId || undefined,
              variantName: item.variantName || undefined,
            })),
            totalAmount: getTotalWithDelivery(),
            deliveryMethod: deliveryMethod,
            deliveryFee: deliveryMethod === 'delivery' ? DELIVERY_FEE : 0,
            customerId: customerId || undefined,
          }),
        });

        const completeResult = await completeResponse.json().catch(() => ({}));

        if (!completeResponse.ok) {
          console.error('Error completing purchase:', completeResult);
        } else {
          // Non-fatal stock update failures are surfaced here for visibility
          if (completeResult.stockErrors?.length > 0) {
            console.error('Stock update issues (purchase was saved):', completeResult.stockErrors);
          }

          // Send confirmation emails (non-blocking — never fails the flow)
          fetch('/api/send-purchase-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customerInfo: customerInfoForAPI,
              items: cartItems.map(item => ({
                id: item.id,
                name: item.name,
                quantity: item.quantity || 1,
                price: item.price,
                isPreorder: item.isPreorder || false,
                image: item.image || null,
                digital_token: item.digital_token || null,
                source: item.source || undefined,
                ally_id: item.ally_id || undefined,
                variantName: item.variantName || undefined,
                variantImage: item.variantImage || undefined,
              })),
              totalAmount: getTotalWithDelivery(),
              paymentIntent: {
                id: intent.id,
                amount: intent.amount,
                currency: intent.currency,
                status: intent.status,
              },
              deliveryMethod: deliveryMethod,
              deliveryFee: deliveryMethod === 'delivery' ? DELIVERY_FEE : 0,
            }),
          }).catch(err => console.error('Email service error (non-blocking):', err));
        }
      } catch (completeError) {
        console.error('Error calling complete-purchase API:', completeError);
      } finally {
        setUpdatingInventory(false);
      }

      // Store purchased items with digital_token before clearing cart
      const itemsWithDigitalToken = cartItems.map(item => ({
        ...item,
        digital_token: item.digital_token || null
      }));
      setPurchasedItems(itemsWithDigitalToken);

      // Set payment success and clear cart
      setPaymentSuccess(true);
      if (isKiosk) setShowKioskVideo(true);
      clearCart();
      clarity.event('purchase_completed');
      // Prioritize this session past Clarity's sampling — a completed
      // purchase is exactly the kind of session worth always being able
      // to replay (docs/ui:ux_feature.md §2.2).
      clarity.upgrade('purchase_completed');
    } catch (error) {
      console.error('Error in handlePaymentSuccess:', error);
      // Still show success since payment was successful
      setPaymentSuccess(true);
      if (isKiosk) setShowKioskVideo(true);
      clearCart();
    }
  };

  const handleAllyOrder = async () => {
    setAllyOrderError(null);
    setAllyOrderSubmitting(true);
    try {
      const { customerInfoForAPI, customerId } = await prepareCustomer(customerInfo);
      const order = await createAllyOrder({
        customerInfo: customerInfoForAPI,
        items: items.map((item) => ({ id: item.id, quantity: item.quantity || 1 })),
        deliveryMethod,
        paymentMethod: selectedAllyPayment,
        customerId: customerId || undefined,
      });
      setAllyOrder(order);
      clearCart();
      clarity.event(selectedAllyPayment === 'bank_transfer' ? 'ally_transfer_order_created' : 'mp_link_order_created');
    } catch (err) {
      setAllyOrderError(err.message);
    } finally {
      setAllyOrderSubmitting(false);
    }
  };

  const handleCopyClabe = async (clabe) => {
    try {
      await navigator.clipboard.writeText(clabe);
      setCopiedClabe(clabe);
    } catch {
      // Clipboard blocked — the CLABE is on screen and in the email.
    }
  };

  if (allyOrder && allyOrder.paymentMethod === 'bank_transfer') {
    const multiple = allyOrder.transfers.length > 1;
    return (
      <div className="container" style={{ padding: '2rem 0' }}>
        <div style={{
          margin: '0 auto', maxWidth: '520px', padding: '1.5rem', background: 'white',
          borderRadius: '12px', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        }}>
          <h1 style={{ fontSize: '1.6rem', color: '#333', marginBottom: '0.75rem', fontFamily: "'Gambado Sans', sans-serif" }}>
            {t('mpLink.successTitle')}
          </h1>
          {allyOrder.orderCode && (
            <p style={{ color: '#666', marginBottom: '0.75rem' }}>{t('mpLink.orderCode', { code: allyOrder.orderCode })}</p>
          )}
          <p style={{ color: '#444', marginBottom: '1.25rem', lineHeight: 1.5 }}>
            {multiple ? t('transfer.introMultiple') : t('transfer.intro')}
          </p>
          {allyOrder.emailSent && customerInfo?.email && (
            <p style={{ color: '#28a745', fontSize: '0.9rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
              {t('transfer.emailSent', { email: customerInfo.email })}
            </p>
          )}
          {allyOrder.lines.map((line) => (
            <div key={line.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
              <span>{line.name} <span style={{ color: '#888' }}>× {line.quantity}</span></span>
              <span style={{ fontWeight: '600', flexShrink: 0 }}>{formatPrice(line.price * line.quantity)}</span>
            </div>
          ))}
          {allyOrder.transfers.map((transfer) => (
            <div key={transfer.allyId} style={{ marginTop: '1rem', padding: '1rem', background: '#f7f9fc', border: '1px solid #e3e9f3', borderRadius: '8px' }}>
              <div style={{ fontWeight: '700', marginBottom: '0.5rem' }}>
                {multiple ? t('transfer.headingAlly', { ally: transfer.allyName }) : t('transfer.heading')}
              </div>
              <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 0.75rem', margin: 0, fontSize: '0.95rem' }}>
                {transfer.bankName && (<><dt style={{ color: '#666' }}>{t('transfer.bank')}</dt><dd style={{ margin: 0 }}>{transfer.bankName}</dd></>)}
                {transfer.beneficiary && (<><dt style={{ color: '#666' }}>{t('transfer.beneficiary')}</dt><dd style={{ margin: 0 }}>{transfer.beneficiary}</dd></>)}
                <dt style={{ color: '#666' }}>{t('transfer.clabe')}</dt>
                <dd style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <code style={{ fontSize: '1rem', fontWeight: '700', letterSpacing: '0.05em', wordBreak: 'break-all' }}>{transfer.clabe}</code>
                  <button
                    type="button"
                    onClick={() => handleCopyClabe(transfer.clabe)}
                    style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem', border: '1px solid #2ca9e1', color: '#2ca9e1', background: 'white', borderRadius: '6px', cursor: 'pointer' }}
                  >
                    {copiedClabe === transfer.clabe ? t('transfer.copied') : t('transfer.copy')}
                  </button>
                </dd>
                <dt style={{ color: '#666' }}>{t('transfer.amount')}</dt>
                <dd style={{ margin: 0, fontWeight: '700', color: '#2ca9e1' }}>{formatPrice(transfer.amount)}</dd>
                <dt style={{ color: '#666' }}>{t('transfer.reference')}</dt>
                <dd style={{ margin: 0, fontWeight: '600' }}>{transfer.reference}</dd>
              </dl>
            </div>
          ))}
          <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#333', lineHeight: 1.5 }}>{t('transfer.proofNote')}</p>
          {allyOrder.deliveryFee > 0 && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>
              {t('transfer.deliveryFeeNote', { fee: formatPrice(allyOrder.deliveryFee) })}
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.1rem', color: '#2ca9e1', marginTop: '1rem' }}>
            <span>{t('orderSummary.total')}</span>
            <span>{formatPrice(allyOrder.total)}</span>
          </div>
          <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#888', lineHeight: 1.5 }}>{t('transfer.confirmationNote')}</p>
        </div>
      </div>
    );
  }

  if (allyOrder) {
    const mpLinkOrder = allyOrder;
    return (
      <div className="container" style={{ padding: '2rem 0' }}>
        <div style={{
          margin: '0 auto', maxWidth: '520px', padding: '1.5rem', background: 'white',
          borderRadius: '12px', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        }}>
          <h1 style={{ fontSize: '1.6rem', color: '#333', marginBottom: '0.75rem', fontFamily: "'Gambado Sans', sans-serif" }}>
            {t('mpLink.successTitle')}
          </h1>
          {mpLinkOrder.orderCode && (
            <p style={{ color: '#666', marginBottom: '0.75rem' }}>{t('mpLink.orderCode', { code: mpLinkOrder.orderCode })}</p>
          )}
          <p style={{ color: '#444', marginBottom: '1.25rem', lineHeight: 1.5 }}>{t('mpLink.successIntro')}</p>
          {mpLinkOrder.emailSent && customerInfo?.email && (
            <p style={{ color: '#28a745', fontSize: '0.9rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
              {t('mpLink.emailSent', { email: customerInfo.email })}
            </p>
          )}
          {mpLinkOrder.lines.map((line) => (
            <div key={line.id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: '600' }}>{line.name}</span>
                <span style={{ fontWeight: '600', color: '#2ca9e1', flexShrink: 0 }}>{formatPrice(line.price)}</span>
              </div>
              {line.quantity > 1 && (
                <p style={{ fontSize: '0.85rem', color: '#856404', margin: '0 0 0.5rem' }}>
                  {t('mpLink.payTimes', { count: line.quantity })}
                </p>
              )}
              <MercadoPagoLinkButton
                href={line.mercadoPagoUrl}
                onClick={() => trackClick(`mercado-pago-link-${line.id}`, 'link', 'Pagar con Mercado Pago', {
                  productId: line.id, purchaseId: mpLinkOrder.purchaseId, source: 'ally',
                })}
              >
                {t('mpLink.payButton')}
              </MercadoPagoLinkButton>
            </div>
          ))}
          {mpLinkOrder.deliveryFee > 0 && (
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>
              {t('mpLink.deliveryFeeNote', { fee: formatPrice(mpLinkOrder.deliveryFee) })}
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.1rem', color: '#2ca9e1', marginTop: '1rem' }}>
            <span>{t('orderSummary.total')}</span>
            <span>{formatPrice(mpLinkOrder.total)}</span>
          </div>
          <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#888', lineHeight: 1.5 }}>{t('mpLink.confirmationNote')}</p>
        </div>
      </div>
    );
  }

  if (totalItems === 0 && !paymentSuccess) {
    navigate('/cart');
    return null;
  }

  if (paymentSuccess) {
    const customerName = customerInfo?.name?.split(' ')[0] || t('success.friendFallback');
    
    // Get the first purchased item with a digital_token
    const itemWithDigitalToken = purchasedItems.find(item => item.digital_token);
    const digitalTokenUrl = itemWithDigitalToken?.digital_token;
    
    return (
      <div className="container" style={{ padding: '2rem 0', textAlign: 'center' }}>
        {isKiosk && showKioskVideo && (
          <KioskPurchaseSuccessVideo onEnded={() => setShowKioskVideo(false)} />
        )}
        {updatingInventory ? (
          <>
            <div className="loading" style={{ marginBottom: '2rem' }}></div>
            <h1 style={{ marginBottom: '1rem' }}>
              {t('success.processingTitle')}
            </h1>
            <p style={{ marginBottom: '2rem', fontSize: '1.1rem', color: '#666' }}>
              {t('success.updatingInventory')}
            </p>
          </>
        ) : (
          <>
            <h1 className="success-page" style={{ 
              marginBottom: '1rem', 
              fontSize: '2rem', 
              color: '#333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontFamily: "'Gambado Sans', sans-serif"
            }}>{t('success.thanks', { name: customerName })}
            </h1>
            
            {/* Digital Token Image */}
            {digitalTokenUrl && (
              <div className="digital-token-image" style={{ 
                margin: '1rem auto',
                maxWidth: '450px',
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}>
                
                  <img
                    src={digitalTokenUrl}
                    alt={t('success.digitalTokenAlt')}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block'
                    }}
                    onError={(e) => {
                      console.error('Error loading digital token image:', digitalTokenUrl);
                      e.target.style.display = 'none';
                    }}
                  />
              </div>
            )}

            {/* Order details */}
            {purchasedItems.length > 0 && (
              <div className="order-details" style={{
                margin: '2rem auto',
                maxWidth: '500px',
                width: '100%',
                padding: '1.5rem',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                textAlign: 'left'
              }}>
                <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem' }}>{t('orderSummary.heading')}</h2>
                {purchasedItems.map(item => {
                  const displayName = tField(item, 'name', locale) || item.name;
                  return (
                  <div key={item.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid #eee'
                  }}>
                    <img
                      src={proxyThumb(item.image)}
                      alt={displayName}
                      loading="lazy"
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        flexShrink: 0,
                        background: '#f4f4f4'
                      }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/56x56/f8f9fa/333?text=Producto';
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 style={{ marginBottom: '0.25rem', fontSize: '1rem' }}>
                        {item.isPreorder ? t('orderSummary.itemDeposit', { name: displayName }) : displayName}
                      </h4>
                      <p style={{ color: '#666', fontSize: '0.875rem' }}>{t('orderSummary.quantity', { count: item.quantity })}</p>
                    </div>
                    <p style={{ fontWeight: '600', color: '#2ca9e1', flexShrink: 0 }}>
                      {formatPrice(item.price * item.quantity)}
                    </p>
                  </div>
                  );
                })}
                <div style={{ marginTop: '1rem', paddingTop: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>{t('orderSummary.subtotal')}</span>
                    <span>{formatPrice(purchasedItems.reduce((s, i) => s + i.price * (i.quantity || 1), 0))}</span>
                  </div>
                  {deliveryMethod === 'delivery' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>{t('orderSummary.shipping')}</span>
                      <span>{formatPrice(DELIVERY_FEE)}</span>
                    </div>
                  )}
                  {deliveryMethod === 'personal' && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: '#28a745', fontSize: '0.9rem' }}>
                        <span>{t('orderSummary.personalLabel')}</span>
                        <span>{t('orderSummary.personalNoCost')}</span>
                      </div>
                      <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                        <div style={{ fontWeight: '600' }}>{t('delivery.personal.locationName')}</div>
                        <div>{t('delivery.personal.address')}</div>
                        <div>{t('delivery.personal.hours')}</div>
                      </div>
                    </>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.1rem', color: '#2ca9e1', marginTop: '0.5rem' }}>
                    <span>{t('orderSummary.total')}</span>
                    <span>{formatPrice(deliveryMethod === 'personal' ? purchasedItems.reduce((s, i) => s + i.price * (i.quantity || 1), 0) : purchasedItems.reduce((s, i) => s + i.price * (i.quantity || 1), 0) + DELIVERY_FEE)}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Social Sharing Section */}
            {digitalTokenUrl && (
              <div className="social-sharing" style={{ 
                margin: '2rem 0',
                width: '100%',
                padding: '1rem',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{ 
                  marginBottom: '1rem', 
                  fontSize: '1.3rem',
                  color: '#333'
                }}>
                  {t('success.shareCta')}
                </h3>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="container checkout-page">

      <h1 style={{ 
        marginBottom: '2rem', 
        fontSize: '2.5rem',
        fontFamily: "'Gambado Sans', sans-serif"
      }}>
        {t('title')}
      </h1>

      <div className="checkout-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '3rem',
        alignItems: 'start'
      }}>
        {/* Left Column: Customer Info and Payment */}
        <div>
          {/* Delivery Method Selection */}
          <div style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            marginBottom: '2rem'
          }}>
            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', color: '#333' }}>
              {t('delivery.heading')}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '1rem',
                border: `2px solid ${deliveryMethod === 'delivery' ? '#2ca9e1' : '#ddd'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: deliveryMethod === 'delivery' ? '#f0f8ff' : 'white'
              }}>
                <input
                  type="radio"
                  name="deliveryMethod"
                  value="delivery"
                  checked={deliveryMethod === 'delivery'}
                  onChange={(e) => setDeliveryMethod(e.target.value)}
                  style={{ marginRight: '0.75rem', width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{t('delivery.delivery.label')}</div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>{t('delivery.delivery.cost', { fee: formatPrice(DELIVERY_FEE) })}</div>
                </div>
              </label>
              
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '1rem',
                border: `2px solid ${deliveryMethod === 'personal' ? '#2ca9e1' : '#ddd'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: deliveryMethod === 'personal' ? '#f0f8ff' : 'white'
              }}>
                <input
                  type="radio"
                  name="deliveryMethod"
                  value="personal"
                  checked={deliveryMethod === 'personal'}
                  onChange={(e) => setDeliveryMethod(e.target.value)}
                  style={{ marginRight: '0.75rem', width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{t('delivery.personal.label')}</div>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                    <div style={{ fontWeight: '600' }}>{t('delivery.personal.locationName')}</div>
                    <div>{t('delivery.personal.address')}</div>
                    <div>{t('delivery.personal.hours')}</div>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Customer Info Form */}
          <div style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
                {t('contact.heading')}
              </h3>
              {authUser ? (
                <button
                  type="button"
                  onClick={() => navigate('/account')}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem',
                    background: 'transparent',
                    color: '#2ca9e1',
                    border: '1px solid #2ca9e1',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {t('contact.myAccount')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/account')}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem',
                    background: '#2ca9e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {t('contact.login')}
                </button>
              )}
            </div>
            <CustomerInfoForm 
              onCustomerInfoChange={handleCustomerInfoChange}
              deliveryMethod={deliveryMethod}
              requireInstagram={requireInstagram}
              prefillData={loadedCustomer || undefined}
              hideTitle
            />
            {!authUser && (
              <label style={{ display: 'flex', alignItems: 'center', marginTop: '1.25rem', cursor: 'pointer', fontSize: '0.95rem', color: '#333' }}>
                <input
                  type="checkbox"
                  checked={saveMyData}
                  onChange={(e) => setSaveMyData(e.target.checked)}
                  style={{ marginRight: '0.5rem', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                {t('contact.saveData')}
              </label>
            )}
            <label style={{ display: 'flex', alignItems: 'flex-start', marginTop: '0.75rem', cursor: 'pointer', fontSize: '0.95rem', color: '#333', lineHeight: 1.4 }}>
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                style={{ marginRight: '0.5rem', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
              />
              {t('contact.marketingOptIn')}
            </label>
          </div>

          {/* Payment Form */}
          <div style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ marginBottom: '1.5rem' }}>{t('payment.heading')}</h2>
            {!isCustomerInfoValid && (
              <div style={{ 
                padding: '1rem', 
                background: '#fff3cd', 
                border: '1px solid #ffc107', 
                borderRadius: '8px',
                color: '#856404',
                marginBottom: '1rem'
              }}>
                {t('payment.missingInfo')}
              </div>
            )}
            {isMixedBag ? (
              <p style={{ padding: '1rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', color: '#856404', margin: 0 }}>
                {t('allyPayment.mixedBag')}
              </p>
            ) : isAllyBag && allyPaymentOptions.length === 0 ? (
              <p style={{ padding: '1rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', color: '#856404', margin: 0 }}>
                {t('allyPayment.unavailable')}
              </p>
            ) : isAllyBag ? (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  {[
                    allyPaymentOptions.includes('mercado_pago_link') && { value: 'mercado_pago_link', label: t('mpLink.methodLink'), hint: t('mpLink.methodLinkHint') },
                    allyPaymentOptions.includes('bank_transfer') && {
                      value: 'bank_transfer',
                      label: t('transfer.methodLabel'),
                      hint: allyNames ? t('transfer.methodHint', { ally: allyNames }) : t('transfer.methodHintNoName'),
                    },
                  ].filter(Boolean).map((option) => (
                    <label key={option.value} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '1rem',
                      border: `2px solid ${selectedAllyPayment === option.value ? '#2ca9e1' : '#ddd'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: selectedAllyPayment === option.value ? '#f0f8ff' : 'white'
                    }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={option.value}
                        checked={selectedAllyPayment === option.value}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        style={{ marginRight: '0.75rem', width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                      <div>
                        <div style={{ fontWeight: '600' }}>{option.label}</div>
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>{option.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {deliveryMethod === 'delivery' && (
                  <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '1rem', lineHeight: 1.5 }}>
                    {selectedAllyPayment === 'bank_transfer'
                      ? t('transfer.deliveryFeeNote', { fee: formatPrice(DELIVERY_FEE) })
                      : t('mpLink.deliveryFeeNote', { fee: formatPrice(DELIVERY_FEE) })}
                  </p>
                )}
                {allyOrderError && (
                  <p style={{ color: '#dc3545', fontSize: '0.9rem', marginBottom: '1rem' }}>{allyOrderError}</p>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAllyOrder}
                  disabled={!isCustomerInfoValid || allyOrderSubmitting}
                  style={{ width: '100%', opacity: !isCustomerInfoValid || allyOrderSubmitting ? 0.6 : 1 }}
                >
                  {allyOrderSubmitting ? t('payment.processing') : t('mpLink.confirmButton')}
                </button>
              </div>
            ) : (
              <StripeCheckout
                totalAmount={getTotalWithDelivery()}
                items={items.map(i => ({ id: i.id, quantity: i.quantity || 1, source: i.source }))}
                deliveryMethod={deliveryMethod}
                onSuccess={handlePaymentSuccess}
                disabled={!isCustomerInfoValid}
              />
            )}
          </div>
        </div>

        {/* Right Column: Order Summary */}
        <div style={{ 
          background: 'white', 
          padding: '2rem', 
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          position: 'sticky',
          top: '2rem'
        }}>
          <h2 style={{ marginBottom: '1.5rem' }}>{t('orderSummary.heading')}</h2>
          
          {items.map(item => {
            const displayName = tField(item, 'name', locale) || item.name;
            return (
            <div key={`${item.source || 'product'}-${item.id}-${item.variantId || ''}`} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 0',
              borderBottom: '1px solid #eee'
            }}>
              <div>
                <h4 style={{ marginBottom: '0.25rem' }}>
                  {item.isPreorder ? t('orderSummary.itemDeposit', { name: displayName }) : displayName}
                  {item.variantName && (
                    <span style={{ fontWeight: '400', color: '#666' }}> — {item.variantName}</span>
                  )}
                </h4>
                <p style={{ color: '#666', fontSize: '0.9rem' }}>
                  {t('orderSummary.quantity', { count: item.quantity })}
                </p>
              </div>
              <p style={{ fontWeight: '600', color: '#2ca9e1' }}>
                {formatPrice(item.price * item.quantity)}
              </p>
            </div>
            );
          })}

          <div style={{ 
            padding: '1rem 0',
            marginTop: '1rem'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '0.5rem' 
            }}>
              <span>{t('orderSummary.subtotal')}</span>
              <span>{formatPrice(getTotalPrice())}</span>
            </div>
            {deliveryMethod === 'delivery' && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem'
              }}>
                <span>{t('orderSummary.shipping')}</span>
                <span>{formatPrice(DELIVERY_FEE)}</span>
              </div>
            )}
            {deliveryMethod === 'personal' && (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                  color: '#28a745',
                  fontSize: '0.9rem'
                }}>
                  <span>{t('orderSummary.personalLabel')}</span>
                  <span>{t('orderSummary.personalNoCost')}</span>
                </div>
                <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                  <div style={{ fontWeight: '600' }}>{t('delivery.personal.locationName')}</div>
                  <div>{t('delivery.personal.address')}</div>
                  <div>{t('delivery.personal.hours')}</div>
                </div>
              </>
            )}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: '700',
              fontSize: '1.2rem',
              color: '#2ca9e1'
            }}>
              <span>{t('orderSummary.total')}</span>
              <span>{formatPrice(getTotalWithDelivery())}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
